import axios from 'axios';
import {
  db,
  doc,
  setDoc,
  getDoc,
  collection,
  writeBatch,
  getDocs,
} from '../firebaseConfig';

const API_KEY = process.env.REACT_APP_API_KEY;
const BASE_URL = process.env.REACT_APP_BASE_URL;

const symbols = [
  { value: 'AAPL', label: 'AAPL' },
  { value: 'MSFT', label: 'MSFT' },
  { value: 'GOOGL', label: 'GOOGL' },
  { value: 'AMZN', label: 'AMZN' },
  { value: 'TSLA', label: 'TSLA' },
  { value: 'NVDA', label: 'NVDA' },
  { value: 'JPM', label: 'JPM' },
  { value: 'WMT', label: 'WMT' },
  { value: 'EURUSD', label: 'EURUSD' },
  { value: 'GBPUSD', label: 'GBPUSD' },
  { value: 'USDJPY', label: 'USDJPY' },
  { value: 'AUDUSD', label: 'AUDUSD' },
  { value: 'USDCHF', label: 'USDCHF' },
  { value: 'USDCAD', label: 'USDCAD' },
  { value: 'AUDCAD', label: 'AUDCAD' },
  { value: 'AUDNZD', label: 'AUDNZD' },
  { value: 'NZDUSD', label: 'NZDUSD' },
  { value: 'GBPJPY', label: 'GBPJPY' },
  { value: 'AUDJPY', label: 'AUDJPY' },
  { value: 'EURGBP', label: 'EURGBP' },
  { value: 'EURAUD', label: 'EURAUD' },
  { value: 'GBPAUD', label: 'GBPAUD' },
  { value: 'GBPCHF', label: 'GBPCHF' },
  { value: 'EURCHF', label: 'EURCHF' },
  { value: 'NZDCAD', label: 'NZDCAD' },
  { value: 'NZDJPY', label: 'NZDJPY' },
  { value: 'CADJPY', label: 'CADJPY' },
  { value: 'CHFJPY', label: 'CHFJPY' },
  { value: 'XAUUSD', label: 'XAUUSD' },
  { value: 'XAGUSD', label: 'XAGUSD' },
  { value: 'WTIUSD', label: 'WTIUSD' },
  { value: 'BRENTUSD', label: 'BRENTUSD' },
  { value: 'COPPERUSD', label: 'COPPERUSD' },
  { value: 'SUGARUSD', label: 'SUGARUSD' },
  { value: 'NGASUSD', label: 'NGASUSD' },
];

const timeframes = ['1min', '5min', '15min', '30min', '1h', '4h', '1day'];

export const mapIntervalToAPI = (interval) => {
  const mapping = {
    '1': '1min',
    '5': '5min',
    '15': '15min',
    '30': '30min',
    '45': '45min',
    '60': '1h',
    '120': '2h',
    '240': '4h',
    '480': '8h',
    '1D': '1day',
    'W': '1week',
    'M': '1month',
  };
  return mapping[interval] || '15min';
};

export const isDataStaleOrMissing = (lastUpdated) => {
  if (!lastUpdated) return true;
  const lastUpdateTime = new Date(lastUpdated).getTime();
  const currentTime = new Date().getTime();
  const oneDay = 24 * 60 * 60 * 1000;
  return (currentTime - lastUpdateTime) > oneDay;
};

const formatDate = (timestamp) => {
  const timestampNum = Number(timestamp);
  if (isNaN(timestampNum)) {
    console.error('Invalid timestamp:', timestamp);
    return null;
  }
  const date = new Date(timestampNum);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const validateAndFormatData = (marketData) => {
  return marketData
    .filter(
      (bar) =>
        bar &&
        typeof bar.time === 'number' &&
        typeof bar.open === 'number' &&
        typeof bar.high === 'number' &&
        typeof bar.low === 'number' &&
        typeof bar.close === 'number'
    )
    .map((bar) => ({
      time: bar.time,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
      volume: bar.volume ? bar.volume : 0,
    }));
};

const getIntervalInMillis = (interval) => {
  const intervals = {
    '1': 60 * 1000,
    '5': 5 * 60 * 1000,
    '15': 15 * 60 * 1000,
    '30': 30 * 60 * 1000,
    '60': 60 * 60 * 1000,
    '120': 120 * 60 * 1000,
    '240': 240 * 60 * 1000,
    '1D': 24 * 60 * 60 * 1000,
    '1min': 60 * 1000,
    '5min': 5 * 60 * 1000,
    '15min': 15 * 60 * 1000,
    '30min': 30 * 60 * 1000,
    '1h': 60 * 60 * 1000,
    '4h': 240 * 60 * 1000,
    '1day': 24 * 60 * 60 * 1000,
  };
  return intervals[interval] || 15 * 60 * 1000;
};

export const fetchMarketData = async (
  symbol,
  interval,
  startDateMillis,
  endDateMillis
) => {
  console.log(
    `Fetching market data for ${symbol}, interval: ${interval}, from ${new Date(
      startDateMillis
    ).toISOString()} to ${new Date(endDateMillis).toISOString()}`
  );

  const startDateAsNumber = Number(startDateMillis);
  const endDateAsNumber = Number(endDateMillis);
  if (isNaN(startDateAsNumber) || isNaN(endDateAsNumber)) {
    console.error('Invalid date parameters:', {
      startDateMillis,
      endDateMillis,
    });
    return [];
  }

  const isStock = [
    'AAPL',
    'MSFT',
    'GOOGL',
    'AMZN',
    'TSLA',
    'NVDA',
    'JPM',
    'WMT',
  ].includes(symbol);
  const formattedSymbol = isStock
    ? symbol
    : `${symbol.slice(0, 3)}/${symbol.slice(3)}`;
  console.log('Formatted symbol for API:', formattedSymbol);

  const formattedStartDate = formatDate(startDateAsNumber);
  const formattedEndDate = formatDate(endDateAsNumber);
  console.log('Formatted dates:', { formattedStartDate, formattedEndDate });

  try {
    const params = {
      apikey: API_KEY,
      interval,
      symbol: formattedSymbol,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      timezone: 'UTC',
      format: 'JSON',
    };
    console.log('API request params:', params);
    const response = await axios.get(`${BASE_URL}/time_series`, { params });
    console.log('API response status:', response.status, 'Meta:', response.data.meta);
    console.log('API response values (first 2, last 2):', response.data.values.slice(0, 2).concat(response.data.values.slice(-2)));

    if (response.data.code && response.data.message) {
      console.error('API Error:', response.data.code, response.data.message);
      return [];
    }
    if (!response.data.values) {
      console.error('No values in API response:', response.data);
      return [];
    }

    const data = response.data.values || [];
    console.log(`Fetched ${data.length} bars`, data.slice(0, 2));

    return data.map((bar) => {
      const barDate = new Date(bar.datetime + 'Z');
      return {
        time: Math.floor(barDate.getTime() / 1000),
        open: parseFloat(bar.open),
        high: parseFloat(bar.high),
        low: parseFloat(bar.low),
        close: parseFloat(bar.close),
        volume: bar.volume ? parseFloat(bar.volume) : 0,
      };
    });
  } catch (error) {
    console.error('Error fetching market data:', error.response?.data || error.message);
    return [];
  }
};

export const fetchAndStoreMarketData = async (
  userID,
  sessionId,
  symbol,
  selectedInterval = '15',
  startDateMillis,
  endDateMillis,
  countBack = null
) => {
  const apiInterval = mapIntervalToAPI(selectedInterval);

  console.log('fetchAndStoreMarketData called with:', {
    userID,
    sessionId,
    symbol,
    selectedInterval,
    startDateMillis: startDateMillis
      ? new Date(startDateMillis).toISOString()
      : 'invalid',
    endDateMillis: endDateMillis
      ? new Date(endDateMillis).toISOString()
      : 'invalid',
    countBack,
  });

  let startDateAsNumber = Number(startDateMillis);
  let endDateAsNumber = Number(endDateMillis) || Date.now();

  if (isNaN(startDateAsNumber) || isNaN(endDateAsNumber)) {
    console.error('Invalid date parameters:', {
      startDateMillis,
      endDateMillis,
    });
    throw new Error('Invalid start or end date');
  }

  // Adjust start date for forex weekend (Sunday 22:00 UTC)
  const startDate = new Date(startDateAsNumber);
  if (startDate.getUTCDay() === 6 || startDate.getUTCDay() === 0) {
    startDate.setUTCDate(
      startDate.getUTCDate() + (startDate.getUTCDay() === 6 ? 1 : 0)
    );
    startDate.setUTCHours(22, 0, 0, 0);
    startDateAsNumber = startDate.getTime();
    console.log(
      `Adjusted start date to Sunday 22:00 UTC: ${startDate.toISOString()}`
    );
  }

  const symbolRef = doc(db, 'Users', userID, 'backtesting', sessionId);
  const intervalRef = doc(symbolRef, `intervals/${selectedInterval}`);
  const barsCollectionRef = collection(intervalRef, 'bars');

  const intervalDoc = await getDoc(intervalRef);
  if (!intervalDoc.exists() || isDataStaleOrMissing(intervalDoc.data()?.lastUpdated)) {
    const intervalMillis = getIntervalInMillis(selectedInterval);
    const rangeMillis = endDateAsNumber - startDateAsNumber;
    const barsInRange = Math.ceil(rangeMillis / intervalMillis);
    const barsNeeded = countBack || barsInRange;
    console.log(`Calculated bars needed: ${barsNeeded} (range: ${barsInRange})`);

    const MAX_BARS_PER_REQUEST = 5000;
    let allMarketData = [];
    let currentStartMillis = startDateAsNumber;
    let attempts = 0;
    const maxAttempts = 3;

    while (
      allMarketData.length < barsNeeded &&
      currentStartMillis < endDateAsNumber &&
      attempts < maxAttempts
    ) {
      const chunkEndMillis = Math.min(
        currentStartMillis + MAX_BARS_PER_REQUEST * intervalMillis,
        endDateAsNumber
      );
      console.log(
        `Fetching chunk ${attempts + 1}/${maxAttempts}: ${new Date(
          currentStartMillis
        ).toISOString()} to ${new Date(chunkEndMillis).toISOString()}`
      );

      const chunkData = await fetchMarketData(
        symbol,
        apiInterval,
        currentStartMillis,
        chunkEndMillis
      );
      console.log(
        `Chunk ${attempts + 1} fetched: ${
          chunkData.length
        } bars, First: ${chunkData[0] ? new Date(chunkData[0].time * 1000).toISOString() : 'N/A'}, Last: ${
          chunkData[chunkData.length - 1]
            ? new Date(chunkData[chunkData.length - 1].time * 1000).toISOString()
            : 'N/A'
        }`
      );

      allMarketData = [...allMarketData, ...chunkData];
      currentStartMillis = chunkEndMillis;
      attempts++;

      if (chunkData.length === 0 || chunkEndMillis >= endDateAsNumber) break;
    }

    if (allMarketData.length > 0) {
      const validatedData = validateAndFormatData(allMarketData);
      const filteredData = validatedData.filter(
        (bar) =>
          bar.time * 1000 >= startDateAsNumber &&
          bar.time * 1000 <= endDateAsNumber
      );
      console.log(
        `Filtered data from ${new Date(
          startDateAsNumber
        ).toISOString()} to ${new Date(endDateAsNumber).toISOString()}`
      );
      console.log(`Total bars after filtering: ${filteredData.length}`);

      const MAX_BYTES_PER_CHUNK = 800 * 1024;
      const avgBarSize = JSON.stringify(filteredData[0]).length;
      const estBarsPerChunk = Math.floor(MAX_BYTES_PER_CHUNK / avgBarSize);
      const CHUNK_SIZE = Math.max(1, Math.min(5000, estBarsPerChunk));
      console.log(
        `Estimated bar size: ${avgBarSize} bytes, chunk size: ${CHUNK_SIZE} bars`
      );

      const chunks = [];
      for (let i = 0; i < filteredData.length; i += CHUNK_SIZE) {
        chunks.push(filteredData.slice(i, i + CHUNK_SIZE));
      }

      const BATCH_SIZE = 400;
      for (let i = 0; i < chunks.length; i++) {
        const batch = writeBatch(db);
        const chunk = chunks[i];
        const chunkRef = doc(barsCollectionRef, `chunk_${i}`);
        batch.set(chunkRef, { data: chunk });

        if (i === chunks.length - 1) {
          batch.set(intervalRef, {
            lastUpdated: new Date().toISOString(),
            totalBars: filteredData.length,
            chunkCount: chunks.length,
            chunkSize: CHUNK_SIZE,
          });
        }

        try {
          await batch.commit();
          console.log(`Committed chunk ${i + 1}/${chunks.length}`);
        } catch (error) {
          console.error(`Error committing chunk ${i + 1}:`, error);
          throw new Error(`Failed to store market data: ${error.message}`);
        }
      }

      console.log(
        `Stored ${filteredData.length} bars across ${chunks.length} chunks for ${symbol} (${selectedInterval})`
      );
      return filteredData;
    } else {
      console.log(`No data fetched for ${symbol} (${selectedInterval})`);
      return [];
    }
  } else {
    const barsSnapshot = await getDocs(barsCollectionRef);
    const existingData = [];
    barsSnapshot.forEach((doc) => existingData.push(...doc.data().data));
    console.log(`Using cached data: ${existingData.length} bars`);
    const filteredData = existingData
      .filter(
        (bar) =>
          bar.time * 1000 >= startDateAsNumber &&
          bar.time * 1000 <= endDateAsNumber
      )
      .sort((a, b) => a.time - b.time);
    if (filteredData.length === 0) {
      console.log('Cached data empty after filtering, refetching from API');
      return await fetchAndStoreMarketData(
        userID,
        sessionId,
        symbol,
        selectedInterval,
        startDateAsNumber,
        endDateAsNumber,
        countBack
      );
    }
    return filteredData;
  }
};